// Shared chart-style helpers for items 99-136 (bar/line/area/pie/funnel/scatter/radar/gauge/treemap/heatmap).
// Pure helpers only — no imports from chartTheme (avoids circular deps).

export const CARTESIAN_TYPES = ["bar", "bar_stacked", "line", "area", "combo"];
export const BAR_TYPES = ["bar", "bar_stacked"];

export const DISPLAY_UNITS = [
  { value: "none", label: "None" },
  { value: "auto", label: "Auto (K / M / B)" },
  { value: "auto_in", label: "Auto Indian (K / L / Cr)" },
  { value: "k", label: "Thousands (K)" },
  { value: "m", label: "Millions (M)" },
  { value: "b", label: "Billions (B)" },
  { value: "lakh", label: "Lakhs (L)" },
  { value: "crore", label: "Crores (Cr)" },
];

export const MARKER_SHAPES = [
  { value: "circle", label: "Circle" },
  { value: "emptyCircle", label: "Hollow circle" },
  { value: "rect", label: "Square" },
  { value: "roundRect", label: "Rounded square" },
  { value: "triangle", label: "Triangle" },
  { value: "diamond", label: "Diamond" },
  { value: "pin", label: "Pin" },
  { value: "arrow", label: "Arrow" },
];

export const LABEL_MODES = [
  { value: "value", label: "Value" },
  { value: "category", label: "Category" },
  { value: "percent", label: "Percent" },
  { value: "cat_value", label: "Category + value" },
  { value: "value_pct", label: "Value + %" },
  { value: "cat_value_pct", label: "Category + value + %" },
];

export const PIE_LABEL_MODES = [
  { value: "cat_pct", label: "Category + %" },
  { value: "category", label: "Category" },
  { value: "value", label: "Value" },
  { value: "percent", label: "Percent" },
  { value: "cat_value", label: "Category + value" },
  { value: "value_pct", label: "Value + %" },
  { value: "cat_value_pct", label: "Category + value + %" },
];

export const FUNNEL_LABEL_MODES = [
  { value: "name", label: "Stage" },
  { value: "value", label: "Value" },
  { value: "percent", label: "% of first stage" },
  { value: "name_value", label: "Stage + value" },
  { value: "name_pct", label: "Stage + %" },
  { value: "name_value_pct", label: "Stage + value + %" },
];

export const SCATTER_LABEL_MODES = [
  { value: "name", label: "Category name" },
  { value: "x", label: "X value" },
  { value: "y", label: "Y value" },
  { value: "name_xy", label: "Name + (x, y)" },
];

// Append an alpha channel to a #rrggbb hex color.
export function hexA(hex, a) {
  const v = Math.round(Math.max(0, Math.min(1, a)) * 255).toString(16).padStart(2, "0");
  return /^#([0-9a-fA-F]{6})$/.test(hex || "") ? hex + v : hex;
}

// Per-series / per-slice color override (options.seriesColors = [{name, color}]).
// Item 7 color priority: series/category override -> widget-level color (fallback) -> theme palette.
export function seriesColorOf(options, name, idx, colors, fallback = null) {
  const ov = (options.seriesColors || []).find((s) => s.name && String(s.name) === String(name) && s.color);
  if (ov) return ov.color;
  return fallback || colors[idx % colors.length];
}

// Configurable gradient fill (direction + end opacity).
export function gradientFill(base, options, horizontal = false) {
  const dir = (options.gradientDir === "horizontal" || horizontal) ? { x: 0, y: 0, x2: 1, y2: 0 } : { x: 0, y: 0, x2: 0, y2: 1 };
  const endOp = typeof options.gradientOpacity === "number" ? options.gradientOpacity : 0.33;
  return { type: "linear", ...dir, colorStops: [{ offset: 0, color: base }, { offset: 1, color: hexA(base, endOp) }] };
}

export function catAxisRotation(options, count) {
  const r = options.labelRotation;
  if (r === null || r === undefined || r === "") return count > 8 ? 30 : 0;
  return Number(r) || 0;
}

export function valueAxisBounds(minV, maxV) {
  const o = {};
  if (minV !== undefined && minV !== null && minV !== "" && !isNaN(Number(minV))) o.min = Number(minV);
  if (maxV !== undefined && maxV !== null && maxV !== "" && !isNaN(Number(maxV))) o.max = Number(maxV);
  return o;
}

// Marker (symbol) props for line / area / radar series.
export function markerConfig(options, fallbackShow, fallbackSize = 7) {
  const show = typeof options.showMarkers === "boolean" ? options.showMarkers : fallbackShow;
  return {
    symbol: show ? (options.markerShape || "circle") : "none",
    symbolSize: Number(options.markerSize) || fallbackSize,
    showSymbol: show,
  };
}

// Item 7: data-label typography is configured independently from axis / legend text.
export const LABEL_ROTATIONS = [0, 45, -45, 90, -90];

export function dataLabelStyle(options, fallbackColor, fallbackSize = 10, fontFamily) {
  return dataLabelStyleFrom(options.dataLabel || {}, fallbackColor, fallbackSize, fontFamily);
}

// Item 22: same typography resolver, but driven by any label-config object (combo bar / line overrides).
export function dataLabelStyleFrom(dl, fallbackColor, fallbackSize = 10, fontFamily) {
  const rot = Number(dl.rotate);
  return {
    color: dl.color || fallbackColor,
    fontSize: Number(dl.fontSize) || fallbackSize,
    fontFamily: dl.family || fontFamily,
    fontWeight: dl.bold ? "bold" : "normal",
    ...(dl.italic ? { fontStyle: "italic" } : {}),
    rotate: LABEL_ROTATIONS.includes(rot) ? rot : 0,
    ...(dl.bgColor ? { backgroundColor: dl.bgColor, padding: [2, 4], borderRadius: 3 } : {}),
  };
}

// Data-label content builder for cartesian charts (item 103).
export function cartesianLabelFormatter({ mode, fmtVal, pctBase }) {
  return (p) => {
    const raw = Array.isArray(p.value) ? p.value[p.value.length - 1] : (p.value?.value ?? p.value);
    const v = Number(raw);
    if (!Number.isFinite(v)) return "";
    const m = mode || "value";
    const pct = () => {
      const base = typeof pctBase === "function" ? pctBase(p) : pctBase;
      return base ? ((v / base) * 100).toFixed(1) + "%" : "";
    };
    if (m === "category") return p.name;
    if (m === "percent") return pct();
    if (m === "cat_value") return `${p.name}: ${fmtVal(v, p)}`;
    if (m === "value_pct") return `${fmtVal(v, p)} (${pct()})`;
    if (m === "cat_value_pct") return `${p.name}: ${fmtVal(v, p)} (${pct()})`;
    return fmtVal(v, p);
  };
}

// Pie / donut label content (item 117).
export function pieLabelFormatter(mode, fmtVal) {
  return (p) => {
    const v = Number(p.value);
    const d = p.percent != null ? p.percent.toFixed(1) + "%" : "";
    switch (mode || "cat_pct") {
      case "category": return p.name;
      case "value": return fmtVal(v);
      case "percent": return d;
      case "cat_value": return `${p.name}: ${fmtVal(v)}`;
      case "value_pct": return `${fmtVal(v)} (${d})`;
      case "cat_value_pct": return `${p.name}: ${fmtVal(v)} (${d})`;
      default: return `${p.name}: ${d}`;
    }
  };
}

// Funnel stage label content (item 121). Percent is relative to the largest stage.
export function funnelLabelFormatter(mode, maxV, fmtVal) {
  return (p) => {
    const v = Number(p.value);
    const pc = maxV ? ((v / maxV) * 100).toFixed(1) + "%" : "";
    switch (mode || "name") {
      case "value": return fmtVal(v);
      case "percent": return pc;
      case "name_value": return `${p.name}: ${fmtVal(v)}`;
      case "name_pct": return `${p.name} (${pc})`;
      case "name_value_pct": return `${p.name}: ${fmtVal(v)} (${pc})`;
      default: return p.name;
    }
  };
}

// Optional interactive data zoom (drag / scroll) for dense cartesian charts.
// Item 7: when left on "auto" the plot area starts scrolling once the category
// count exceeds what can stay readable — columns scroll horizontally, bars vertically.
export function dataZoomWindow(options, horizontal = false, catCount = 0, autoAllowed = true) {
  const mode = options.dataZoom;
  const limit = Number(options.dataZoomCats) || (horizontal ? 12 : 20);
  if (mode === false || mode === "off") return null;
  const auto = autoAllowed && (mode === undefined || mode === null || mode === "" || mode === "auto");
  const on = mode === true || mode === "on" || (auto && catCount > limit);
  if (!on) return null;
  const pct = catCount > limit ? Math.max(5, Math.min(100, (limit / catCount) * 100)) : 100;
  return { start: 0, end: pct };
}

export function applyDataZoom(option, options, horizontal = false, catCount = 0, autoAllowed = true) {
  const win = dataZoomWindow(options, horizontal, catCount, autoAllowed);
  if (!win) return option;
  const axis = horizontal ? { yAxisIndex: 0 } : { xAxisIndex: 0 };
  // Bars read top-down, so the initial window starts at the first category.
  const range = horizontal ? { start: 100 - win.end, end: 100 } : { start: win.start, end: win.end };
  option.dataZoom = [
    { type: "inside", ...axis, ...range, zoomLock: false },
    { type: "slider", ...axis, ...range, ...(horizontal ? { width: 14, right: 4 } : { height: 14, bottom: 2 }), brushSelect: false, showDetail: false },
  ];
  if (option.grid) {
    if (horizontal) option.grid = { ...option.grid, right: (option.grid.right || 16) + 20 };
    else option.grid = { ...option.grid, bottom: (option.grid.bottom || 36) + 18 };
  }
  return option;
}
